'use client'

import { ReactElement, ReactNode, useContext, useEffect, useState } from 'react'
import 'react-dates/initialize'
import { DayPickerRangeController } from 'react-dates'
import 'react-dates/lib/css/_datepicker.css'

import { useDispatch } from 'react-redux'
import { useWizardSelector } from '@/components/ui/wizard/WizardState'
import { onMobile, onMobileOrTablet } from '@/lib/utils/utils'
import classNames from 'classnames'
import { setEndDate, setStartDate } from '@/components/ui/wizard/WizardActions'
import { ConfigContext } from '@/components/common/ConfigContext'
import moment, { Moment } from 'moment'
import MoreDatesButtonMobile from '@/components/ui/MoreDatesButtonMobile'
import { dayIsOutsideBookingWindow, inOneYear, todayPlusTwoDays } from '@/lib/utils/dateUtilsMoment'
import { useTranslations } from 'next-intl'

interface DateRangePickerProps {
  searchWizard: boolean
}

function getOrientation(searchWizard: boolean): 'horizontal' | 'verticalScrollable' {
  const mobileView = searchWizard ? onMobile() : onMobileOrTablet()

  return mobileView ? 'verticalScrollable' : 'horizontal'
}

function NavWrapper(props: { children: ReactNode; searchWizard: boolean }): ReactElement {
  return (
    <div
      className={classNames({
        'wm:flex wm:w-full wm:items-center wm:justify-center': props.searchWizard,
        'wml:flex wml:w-full wml:items-center wml:justify-center': !props.searchWizard,
      })}
    >
      {props.children}
    </div>
  )
}

export default function DateRangePicker(props: DateRangePickerProps): ReactElement {
  const locale = useContext(ConfigContext)!.locale
  const startDate = useWizardSelector<Moment | undefined>((s) => s.startDate?.locale(locale))
  const endDate = useWizardSelector<Moment | undefined>((s) => s.endDate?.locale(locale))
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate'>('startDate')
  const dispatch = useDispatch()
  const [orientation, setOrientation] = useState(getOrientation(props.searchWizard))
  const t = useTranslations('Wizard')

  useEffect(() => {
    function updateOrientation(): void {
      setOrientation(getOrientation(props.searchWizard))
    }

    window.addEventListener('resize', updateOrientation)

    return () => {
      window.removeEventListener('resize', updateOrientation)
    }
  }, [])

  moment().locale(locale)

  return (
    <DayPickerRangeController
      key={orientation}
      startDate={startDate}
      endDate={endDate}
      onDatesChange={({ startDate, endDate }: { startDate: Moment; endDate: Moment }) => {
        dispatch(setStartDate(startDate?.locale(locale)))
        dispatch(setEndDate(endDate?.locale(locale)))
      }}
      focusedInput={focusedInput}
      onFocusChange={(f) => {
        if (focusedInput === 'endDate') {
          setFocusedInput('startDate')
        } else {
          setFocusedInput(f as 'startDate' | 'endDate')
        }
      }}
      numberOfMonths={onMobile() ? 3 : 2}
      minimumNights={1}
      hideKeyboardShortcutsPanel={true}
      orientation={orientation}
      navPrev={
        <NavWrapper searchWizard={props.searchWizard}>
          <button
            className={classNames(
              'absolute rounded-[100%] md:mt-[12px] md:before:translate-x-[-2px]',
              {
                'wd:z-40 wd:block wd:h-[32px] wd:w-[32px] wd:before:inline-flex wd:before:h-[12px] wd:before:w-[12px] wd:before:bg-contain wd:before:bg-center wd:before:bg-no-repeat wd:before:content-[""] wd:before:icon-datepicker-arrow_left wd:hover:bg-[#EAEAEA] wm:hidden':
                  props.searchWizard,
                'wdl:z-40 wdl:block wdl:h-[32px] wdl:w-[32px] wdl:before:inline-flex wdl:before:h-[12px] wdl:before:w-[12px] wdl:before:bg-contain wdl:before:bg-center wdl:before:bg-no-repeat wdl:before:content-[""] wdl:before:icon-datepicker-arrow_left wdl:hover:bg-[#EAEAEA] wml:hidden':
                  !props.searchWizard,
              },
            )}
          ></button>
          <MoreDatesButtonMobile
            searchWizard={props.searchWizard}
            label={t('loadEarlierDatesButtonLabel')}
          />
        </NavWrapper>
      }
      navNext={
        <NavWrapper searchWizard={props.searchWizard}>
          <MoreDatesButtonMobile
            searchWizard={props.searchWizard}
            label={t('loadMoreDatesButtonLabel')}
          />
          <button
            className={classNames('absolute right-0 md:mt-[12px] md:before:translate-x-[1px]', {
              'wd:before:content-[" "] wd:z-40 wd:block wd:h-[32px] wd:w-[32px] wd:rounded-[100%] wd:before:inline-flex wd:before:h-[12px] wd:before:w-[12px] wd:before:bg-contain wd:before:bg-center wd:before:bg-no-repeat wd:before:icon-datepicker-arrow_right wd:hover:bg-[#EAEAEA] wm:hidden':
                props.searchWizard,
              'wdl:before:content-[" "] wdl:z-40 wdl:block wdl:h-[32px] wdl:w-[32px] wdl:rounded-[100%] wdl:before:inline-flex wdl:before:h-[12px] wdl:before:w-[12px] wdl:before:bg-contain wdl:before:bg-center wdl:before:bg-no-repeat wdl:before:icon-datepicker-arrow_right wdl:hover:bg-[#EAEAEA] wml:hidden':
                !props.searchWizard,
            })}
          ></button>
        </NavWrapper>
      }
      isOutsideRange={dayIsOutsideBookingWindow(locale)}
      minDate={todayPlusTwoDays(locale)}
      maxDate={inOneYear(locale)}
    />
  )
}
